if (!customElements.get('visual-feature-toggle')) {
  const defaultState = 'on';
  const switchAt = 0.5;
  const prefersReducedMotion = window.matchMedia(
    '(prefers-reduced-motion: reduce)'
  );

  class VisualFeatureToggle extends HTMLElement {
    connectedCallback() {
      this.buttons = Array.from(
        this.querySelectorAll('[data-visual-feature-toggle-btn]')
      );

      this.stateItems = Array.from(
        this.querySelectorAll(
          '[data-visual-feature-media], [data-visual-feature-caption], [data-visual-feature-icon]'
        )
      );

      this.stage = this.closest('[data-visual-feature-stage]');
      this.stickyWrap =
        this.closest('.visual-feature__sticky-wrap') || this;

      if (!this.buttons.length) return;

      this.currentState = null;
      this.ticking = false;

      this.clickHandlers = this.buttons.map((button) => {
        const handler = () => {
          const clickedState = button.dataset.state;

          if (clickedState === this.currentState) return;

          this.setState(clickedState);
        };

        button.addEventListener('click', handler);
        return handler;
      });

      if (this.stage && !prefersReducedMotion.matches) {
        this.onScroll = this.onScroll.bind(this);
        this.onResize = this.onResize.bind(this);

        this.updateStickyOffset();

        this.resizeObserver =
          'ResizeObserver' in window
            ? new ResizeObserver(this.onResize)
            : null;

        if (this.resizeObserver) {
          this.resizeObserver.observe(this);
        }

        window.addEventListener('resize', this.onResize);

        if ('IntersectionObserver' in window) {
          this.intersectionObserver = new IntersectionObserver(
            this.onIntersect.bind(this),
            {
              rootMargin: '50% 0px 50% 0px',
            }
          );

          this.intersectionObserver.observe(this.stage);
        } else {
          this.addScrollListener();
        }
      }

      this.setState(defaultState);
    }

    disconnectedCallback() {
      if (this.intersectionObserver) {
        this.intersectionObserver.disconnect();
      }

      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
      }

      window.removeEventListener('resize', this.onResize);
      this.removeScrollListener();

      if (this.clickHandlers) {
        this.buttons.forEach((button, index) => {
          button.removeEventListener(
            'click',
            this.clickHandlers[index]
          );
        });
      }
    }

    onIntersect(entries) {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          this.addScrollListener();
          this.updateFromScroll();
        } else {
          this.removeScrollListener();
        }
      });
    }

    addScrollListener() {
      if (this.scrollListenerActive) return;

      this.scrollListenerActive = true;

      window.addEventListener('scroll', this.onScroll, {
        passive: true,
      });
    }

    removeScrollListener() {
      if (!this.scrollListenerActive) return;

      this.scrollListenerActive = false;

      window.removeEventListener('scroll', this.onScroll);
    }

    onScroll() {
      if (this.ticking) return;

      this.ticking = true;

      requestAnimationFrame(() => {
        this.updateFromScroll();
        this.ticking = false;
      });
    }

    onResize() {
      this.updateStickyOffset();

      requestAnimationFrame(() => {
        this.updateFromScroll();
      });
    }

    updateStickyOffset() {
  if (!this.stage) return;

  if (window.innerWidth <= 749) {
    this.stickyWrap.style.top = '4rem';
  } else {
    const panelHeight = this.stickyWrap.offsetHeight;

    this.stickyWrap.style.top =
      `calc(100vh - ${panelHeight}px - var(--vf-bottom-gap, 4) * 1rem)`;
  }
}

    updateFromScroll() {
      if (!this.stage) return;

      const stageRect = this.stage.getBoundingClientRect();
      const stickyTop = this.stickyWrap.getBoundingClientRect().top;

      const scrollableDistance = Math.max(
        this.stage.offsetHeight - this.stickyWrap.offsetHeight,
        1
      );

      const scrolledIntoPin = stickyTop - stageRect.top;

      const progress = Math.min(
        Math.max(scrolledIntoPin / scrollableDistance, 0),
        1
      );

      this.setState(
        progress >= switchAt ? 'off' : 'on'
      );
    }

    setState(state) {
      if (state !== 'on' && state !== 'off') return;

      this.currentState = state;

      this.buttons.forEach((button) => {
        const isActive = button.dataset.state === state;

        button.classList.toggle('is-active', isActive);

        button.setAttribute(
          'aria-pressed',
          isActive ? 'true' : 'false'
        );
      });

      this.stateItems.forEach((item) => {
        item.classList.toggle(
          'is-active',
          item.dataset.state === state
        );
      });
    }
  }

  customElements.define(
    'visual-feature-toggle',
    VisualFeatureToggle
  );
}
